import { orderBy, query } from 'firebase/firestore';
import Papa from 'papaparse';

import { functionAuthRequest } from 'services/API';
import { DocumentStatus } from 'types/DocumentStatus';
import { type FilterProperty } from 'types/FilterProperty';
import { PropertyManagerType } from 'types/PropertyManagerType';
import { SelectState } from 'types/SelectState';
import { arrayInPlaceReject } from 'utils/Array';
import { delay } from 'utils/Async';
import { promptForTextFile } from 'utils/FileUtils';
import { numericCompare } from 'utils/Sort';

import Ad, { AdSchema, newAdSchemaBasedOn } from './Ad';
import type Creative from './Creative';
import DocumentCollection from './DocumentCollection';
import {
    areDynamicPropertyDataArraysEqual,
    cloneDynamicPropertyDataArray,
    type DynamicPropertyData,
} from './DynamicProperties/data';
import { type IDynamicProperty } from './DynamicProperties/IDynamicProperty';
import DynamicPropertiesManager from './DynamicProperties/Manager';
import { merge } from './DynamicProperties/merge';
import FilterManager from './FilterManager';
import FirebaseFunctions from './FirebaseFunctions';
import type Template from './Template';

/**
 * Ads Data Store
 * 
 * Filtering: Filtering generally works by scanning all ads and collecting all the possible values on a per "property" basis. 
 * 
 * current - The currently selected ad.
 * filterValues - Values that are currently selected for a specific dynamic data property. This can be multiple.
 * filterProperties - All the collective values that exist in the entire data set for this specific property. If 200 ads use a productImage of 'foo.jpg', and another 100 use 'bar.jpg, this will return an array of ['ball.jpg', 'bar.jpg']
 */

type UID = string;

export type CSVAdRowData = {
    id: string;
    adID: UID;
    template: string;
    dimensions: string;
    label: string;
};

function formatParseError({ type, code, message, row }: Papa.ParseError) {
    let formattedError = `[${type}:${code}] ${message}`;
    if (row != null) {
        formattedError += ` @ row ${row}`;
    }
    return formattedError;
}

function findDuplicateStrings(strings: string[]): string[] {
    // warning: this mutates the input by sorting it - that just happens to
    // be ok because of the specific callers' circumstances. but it's not a
    // general utility.
    const duplicates = new Set<string>();
    strings.sort();
    for (let i = 1; i < strings.length; i++) {
        if (strings[i] === strings[i - 1]) {
            duplicates.add(strings[i]);
        }
    }
    return Array.from(duplicates);
}

function downloadText(filename: string, text: string) {
    const pom = document.createElement('a');
    pom.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(text)
    );
    pom.setAttribute('download', filename);
    pom.click();
}

export default class AdsManager extends DocumentCollection<Ad, AdSchema> {
    // TODO: (original) this should extend Document and use a type variable
    //  to pass in child type.

    // The base declares `parent: DocumentStore<any>`, but our constructor
    // only ever accepts the Creative that owns us. Before this, three
    // different spellings of "my creative" coexisted here - `this.parent`,
    // `this.parent as Creative`, and `root.creative` - and only the first
    // two named the right object. `declare` is type-only and emits nothing,
    // so becomeObservable() sees exactly the own properties it saw before.
    declare parent: Creative;

    query = () => query(this.collectionRef, orderBy('index'));
    ready = false;
    empty = true;
    currentId = '';
    // filtering = false;
    filterUpdate = NaN;
    filters: FilterProperty[] = [];
    filterProperties: FilterProperty[] = [];
    filter: FilterManager = new FilterManager(this);

    // The anchor for shift-click sweep selection. Undefined until the first
    // selection, and declared so - without the initializer this was not an
    // own property when becomeObservable() ran, so it was silently skipped
    // and stayed non-observable while its type claimed it was always
    // present.
    lastSelectedAd: Ad | undefined = undefined;

    currentWorkMessage: string = '';
    selectedFilterProperties: IDynamicProperty<any>[] = [];
    allUniqueAdProperties: IDynamicProperty<any>[] = [];

    // Deliberately left observable. `isWorking` is a getter, so
    // becomeObservable annotates it as a MobX `computed`, and a computed
    // can only be invalidated by observable reads.
    private readonly workMessageStack: string[] = [];

    constructor(parent: Creative) {
        super(parent, Ad);
        this.becomeObservable();
    }

    get filtering() {
        return this.filter.current.properties.length > 0;
    }

    get selectState() {
        let selectedCount = 0;
        for (const ad of this.children) {
            if (ad.selected) selectedCount++;
        }
        if (selectedCount === 0) return SelectState.NONE;
        if (selectedCount === this.children.length) return SelectState.ALL;
        return SelectState.PARTIAL;
    }

    get current() {
        return this.getById(this.currentId);
    }

    get next() {
        return this.getRelativeToCurrent(1);
    }

    get previous() {
        return this.getRelativeToCurrent(-1);
    }

    private getRelativeToCurrent(offset: number): Ad | null {
        const visibleAds = this.filtered;
        const indexOfCurrent = visibleAds.findIndex(
            (ad) => ad.id === this.currentId
        );
        const resolvedIndex = indexOfCurrent + offset;
        if (resolvedIndex < 0 || resolvedIndex >= visibleAds.length)
            return null;
        return visibleAds[resolvedIndex];
    }

    get filtered() {
        const filteredAds = this.filter.getFilteredAds(this.children);
        filteredAds.sort((a, b) => numericCompare(a.data.index, b.data.index));
        return filteredAds;
    }

    get selected() {
        // Note that this is intersected with the current filter state.
        //
        // This *used* to depend on the value of this.filtering, which is a
        // getter that reports that filtering is enabled if
        // `this.filter.current.properties` is non-empty.
        //
        // Note, though, that `this.filtered` already accounts for that by
        // design, transitively due to the internal logic of
        // `FilterManager.getFilteredAds`, which returns all the ads if its
        // current.properties is empty.
        //
        // Implementation-wise, there is a small overhead as
        // `getFilteredAds` still runs its logic for every member, and
        // `this.filtered` sorts the result, but that's an optimization
        // question that can be acted upon without changing the fact that
        // `this.filtered` and
        // `this.filtering ? this.filtered : this.children`
        // return the same (modulo index order)

        return this.filtered.filter((ad) => ad.selected);
    }

    private pushWorkMessage(message: string) {
        this.workMessageStack.push(this.currentWorkMessage);
        this.currentWorkMessage = message;
    }

    private popWorkMessage() {
        this.currentWorkMessage = this.workMessageStack.pop() || '';
    }

    get isWorking() {
        return this.workMessageStack.length > 0;
    }

    addMultipleAds = async (set: AdSchema[], insertIndex?: number) => {
        const children = [...this.filtered];
        const newDocs: Ad[] = [];

        this.selectNone();

        for (const data of set) {
            const newDoc = await this.create(newAdSchemaBasedOn(data));
            newDocs.push(newDoc);
            newDoc.selectToggle();
        }

        if(insertIndex === undefined) {
            children.push(...newDocs);
        } else {
            children.splice(insertIndex, 0, ...newDocs);
        }

        this.children = children;

        this.updateIndexesLocal();
        
        this.status = DocumentStatus.LOADING;
        await this.updateIndexes();
        await this.commitLocal();
        this.status = DocumentStatus.LOADED;
    }

    updateMultiple = async (ads: ReadonlyArray<Ad>) => {
        // Sends every ad in one request. This used to chunk the payloads and fire
        // several requests concurrently, which predates the backend's use of
        // Firestore's BulkWriter (see functions/src/api/bulk.ts). BulkWriter
        // already does rate limiting (the "555" ramp-up) and per-write retry, so
        // client-side chunking was a second, worse implementation of the same
        // concern - and firing N requests concurrently gave us N independent
        // BulkWriters that couldn't coordinate with each other.
        //
        // The cost of this simplification is that the work popup can no longer
        // report a percentage, because there are no longer discrete rounds to
        // count. That's a UI affordance to re-derive deliberately if it's wanted,
        // rather than a side effect of how the transport happened to be shaped.

        this.pushWorkMessage('Updating ads…');

        try {
            const payloads = ads.map((ad) => ({
                index: ad.data.index,
                key: ad.id,
                dynamicData: [...ad.properties.getState().value],
            }));

            await functionAuthRequest(
                'updateMultipleAds',
                { ads: payloads },
                this.parent
            );

            for (const ad of ads) {
                // Warning! This preserves old behavior but is unconditional
                // on the result of /updateMultipleAds, meaning if
                // updateMultipleAds failed either totally or partially,
                // this could result in lost work.
                ad.properties.resetChanged();
            }
        } finally {
            this.popWorkMessage();
        }
    };

    new = async (dynamicProperties?: DynamicPropertyData[]) => {
        // TODO: (original) routing needs to be fixed here.
        console.log(`Ads: Creative new ad.`);
        const data = new AdSchema();
        if (dynamicProperties) data.dynamicData = dynamicProperties;
        // else data.dynamicData = this.parent.dynamicProperties.getState();
        if (this.current) data.index = this.current.data.index + 1;

        const newAd = await this.add(data);

        newAd.navigateTo();
    };

    newFromTemplate = async (template: Template) => {
        console.log('newFromTemplate', template);
        const data = new AdSchema();
        const index = this.current
            ? this.current.data.index + 1
            : this.children.length;
        data.index = index;

        // Create the new Ad instance
        const newAd = await this.create(data);

        // Set up the new ad's properties
        newAd.properties.setState(
            cloneDynamicPropertyDataArray(this.parent.data.dynamicProperties)
        );
        merge(newAd.properties, template.properties, { addMissing: true });

        newAd.properties.getChild('template')?.set(template.id);
        newAd.properties.getChild('label')?.set(template.data.name);
        await newAd.renderAllSVGProperties();
        newAd.saveDynamicProperties(false);

        this.addMultipleAds([newAd.data], index);
    };

    updateIndexesLocal = () => {
        const ads = [...this.children];
        ads.forEach((ad, index) => {
            ad.setIndex(index);
        });
    }

    updateIndexes = async () => {
        // Sends every index in one request, for the same reason as
        // updateMultiple above: the backend's /updateAdIndexes handler uses
        // BulkWriter, so rate limiting and per-write retry are already handled
        // there. This used to chunk at 1000 and loop.
        //
        // It also used to accept a `set: Ad[]` parameter meant to narrow the
        // update to a subset of ads, but the line that would have used it was
        // commented out, every caller passed nothing, and the whole collection
        // was always sent. Removed rather than left as a promise the signature
        // couldn't keep.

        this.pushWorkMessage('Updating Ad IDs…');

        try {
            const adIndexes: [string, number][] = this.children
                .filter((ad) => !ad.isLocalOnly)
                .map((ad) => [ad.id, ad.data.index]);

            await FirebaseFunctions.updateAdIndexes(this.parent, adIndexes);
        } finally {
            this.popWorkMessage();
        }
    };

    exportAndDownloadCSV = () => {
        let creativeName = this.parent.data.name;
        if (!creativeName) {
            const nowTimestamp = new Date().toISOString().replace(/[:.]/g, '-');
            creativeName = `Exported ads ${nowTimestamp}`;
        }
        const data = this.selected.map((ad) => ad.properties.getCSVRowObject());
        downloadText(`${creativeName}.csv`, Papa.unparse(data));
    };

    importCSV = async () => {
        const logPrefix = '[AdsManager.importCSV]';
        const workMessagePrefix = `Importing CSV…`;
        let pushedWorkMessage = false;

        const batchSize = 10;

        try {
            const fileContent = await promptForTextFile();
            if (!fileContent) return;

            const parseResult = Papa.parse<CSVAdRowData>(fileContent, {
                header: true,
                skipEmptyLines: true,
            });
            if (parseResult.errors.length > 0) {
                console.warn(
                    `${logPrefix} CSV parse errors:`,
                    parseResult.errors
                );
                const readableErrors = parseResult.errors.map(formatParseError);
                const errorMessage = `CSV contained errors: ${readableErrors.join('; ')}`;
                throw new Error(errorMessage);
            }

            const { data } = parseResult;
            console.log(`${logPrefix} data:`, data);

            this.pushWorkMessage(`${workMessagePrefix} 0%`);
            pushedWorkMessage = true;

            const duplicateAdIds = findDuplicateStrings(
                data.map((ad) => ad.id)
            );
            const duplicateAdKeys = findDuplicateStrings(
                data.map((ad) => ad.adID)
            );

            if (duplicateAdIds.length > 0 || duplicateAdKeys.length > 0) {
                console.log(
                    `${logPrefix} duplicateAdIds: %O duplicateAdKeys: %O`,
                    duplicateAdIds,
                    duplicateAdKeys
                );
                let errorMessage = 'CSV Import: Duplicate IDs found.';
                if (duplicateAdIds.length > 0) {
                    errorMessage += ` id column: [${duplicateAdIds.join(', ')}]`;
                }
                if (duplicateAdKeys.length > 0) {
                    errorMessage += ` adID column: [${duplicateAdKeys.join(', ')}]`;
                }
                throw new Error(errorMessage);
            }

            for (let i = 0; i < data.length; i += batchSize) {
                console.log(`${logPrefix} batch ${i}`);
                await this.importCSVBatch(i, data, batchSize);

                // it's ok to directly change currentWorkMessage if we've
                // called pushWorkMessage; then we're only stomping on a
                // message we set ourselves
                const completed = Math.min(i + batchSize, data.length);
                const progressPercent = Math.round(
                    (completed * 100) / data.length
                );
                this.currentWorkMessage = `${workMessagePrefix} ${progressPercent}%`;
            }
            console.log(`${logPrefix} Done.`);
        } finally {
            if (pushedWorkMessage) {
                this.popWorkMessage();
            }
        }
    };

    importCSVBatch = async (startIndex: number, data: CSVAdRowData[], batchSize: number = 10) => {
        await delay(10);
        let batchCount = 0;
        for (let i = startIndex; i < data.length && batchCount < batchSize; i++) {
            batchCount++;
            const row = data[i];
            const ad = this.getById(row.adID);

            if (ad) {
                // Owned by the ad, like the ad.properties it's compared with,
                // so it can reach its creative (Ad -> AdsManager -> Creative).
                const newDynamicProperties = new DynamicPropertiesManager(ad);
                newDynamicProperties.setUsingCSVRowObject(row);
                const changed = !areDynamicPropertyDataArraysEqual(
                    newDynamicProperties.properties,
                    ad.properties.properties
                );
                // console.log('##CSVImport: changed', changed);
                if (changed) {
                    try {
                        ad.properties.setUsingCSVRowObject(row);
                        await ad.renderAllSVGProperties();
                        ad.setIndex(Number(row.id) - 1);
                    } catch (error) {
                        throw new Error(String(error));
                    }
                }
            } else {
                throw new Error('CSV Import: No current ad exists for row ' + (i + 2) + '. Please make sure the adID is correct.');
                // let index = lastExistingAd ? lastExistingAd.index + 1 : this.all.length;
                // let newAd = this.newLocal(this.root.creative.dynamicProperties.csvDataToPropertyArray(row), index);
                // lastExistingAd = newAd;
                // console.log('NEWAD', newAd);
            }
        }
    };

    selectAll = () => {
        this.setEverySelectedState(true);
    };

    selectNone = () => {
        this.setEverySelectedState(false);
    };

    setEverySelectedState(newSelectedState: boolean) {
        for (const ad of this.children) {
            ad.setSelected(newSelectedState);
        }
    }

    selectChanged = () => {
        this.selectNone();
        for (const visibleAd of this.filtered) {
            if (visibleAd.properties.getChanged()) {
                visibleAd.setSelected(true);
            }
        }
    };

    select = (selectedAd: Ad, isSweepSelect: boolean) => {
        const lastSelectedAd = this.lastSelectedAd;

        if (isSweepSelect && lastSelectedAd) {
            // Copy children array for sort
            const children = [...this.children];
            children.sort((a, b) => numericCompare(a.data.index, b.data.index));

            const firstIndex = Math.min(
                selectedAd.data.index,
                lastSelectedAd.data.index
            );
            const lastIndex = Math.max(
                selectedAd.data.index,
                lastSelectedAd.data.index
            );

            for (let i = firstIndex; i <= lastIndex; i++) {
                children[i]?.setSelected(lastSelectedAd.selected);
            }
        } else {
            selectedAd.selectToggle();
        }

        this.lastSelectedAd = selectedAd;
    };

    setCurrent = (id: string) => {
        if (!id) {
            this.clearCurrent();
            return;
        }

        const currentAd = this.getById(id);
        if (!currentAd) return false;
        if (currentAd.id === this.currentId) return false;

        this.current?.deactivate();
        currentAd.activate();

        this.currentId = currentAd.id;

        this.parent.setCurrentPropertyManager(PropertyManagerType.AD);
        return true;
    };

    clearCurrent = () => {
        if (this.current) {
            this.current.deactivate();
            this.currentId = '';
        }
    };

    addSelectedFilterProperty = (property: IDynamicProperty<any>) => {
        this.selectedFilterProperties.push(property);
    }

    removeSelectedFilterProperty = (selected: IDynamicProperty<any>) => {
        arrayInPlaceReject(this.selectedFilterProperties, (scan) =>
            selected.value === scan.value &&
            selected.name === scan.name &&
            selected.type === scan.type
        );
    };

    onCreate = (ad: Ad) => {
        this.filter.parseFilterPropertiesFromAd(ad);
    }

    removeMultipleAds = async (set: Ad[]) => {
        await this.removeMultiple(set);
        this.updateIndexesLocal();
        await this.updateIndexes();
    }

    reset() {
        super.reset();
        this.current?.reset();
        this.current?.properties.reset();
        this.empty = true;
        this.currentId = '';
        this.lastSelectedAd = undefined;
        this.filterUpdate = NaN;
        this.filters = [];
        this.filterProperties = [];
        this.allUniqueAdProperties = [];
        this.currentWorkMessage = '';
        this.workMessageStack.splice(0);
    }
}
